/**
 * AuthProvider - Authentication Context Provider
 *
 * Provides authentication state and actions to the entire application.
 * This provider is agnostic to the underlying authentication mechanism,
 * working with any registered AuthAdapter implementation.
 *
 * Features:
 * - Automatic initialization and session restoration
 * - Provider-agnostic authentication interface
 * - Error handling and loading states
 * - MSAL Provider integration for Entra ID
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { MsalProvider } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';
import { AuthContext, type AuthContextValue } from './AuthContext';
import {
  type AuthUser,
  type AuthProviderType,
  type LoginOptions,
  providerRegistry,
  entraAdapter,
} from './adapters';

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
  /** Child components that will have access to auth context */
  children: ReactNode;

  /** Initial provider type to use (defaults to 'entra') */
  initialProvider?: AuthProviderType;
}

/**
 * Internal state for the auth provider
 */
interface InternalAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  roles: string[];
  error: Error | null;
  providerType: AuthProviderType | null;
  msalInstance: PublicClientApplication | null;
}

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication context.
 * Handles initialization, state management, and provider switching.
 */
export function AuthProvider({
  children,
  initialProvider = 'entra',
}: AuthProviderProps) {
  // Internal state
  const [state, setState] = useState<InternalAuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    roles: [],
    error: null,
    providerType: null,
    msalInstance: null,
  });

  /**
   * Initialize the authentication system
   */
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        // Register the Entra adapter (default provider)
        providerRegistry.register(entraAdapter);

        // Set the active provider from environment or props
        const envProvider = import.meta.env.VITE_AUTH_PROVIDER as AuthProviderType | undefined;
        const activeProvider = envProvider || initialProvider;

        try {
          providerRegistry.setActive(activeProvider);
        } catch {
          // If the requested provider isn't available, stick with default
          console.warn(
            `Provider '${activeProvider}' not available, using default`
          );
        }

        // Get MSAL instance for Entra ID
        const msalInstance = await entraAdapter.getMsalInstance();

        // Handle any redirect callbacks (for redirect-based auth)
        await entraAdapter.handleRedirectCallback();

        // Check for existing session
        const adapter = providerRegistry.getActive();
        let user: AuthUser | null = null;
        let roles: string[] = [];
        let isAuthenticated = false;

        if (adapter?.isAuthenticated()) {
          user = await adapter.getUser();
          roles = await adapter.getRoles();
          isAuthenticated = true;
        }

        if (mounted) {
          setState({
            isLoading: false,
            isAuthenticated,
            user,
            roles,
            error: null,
            providerType: providerRegistry.getActiveProviderType(),
            msalInstance,
          });
        }
      } catch (error) {
        console.error('[AuthProvider] Initialization error:', error);
        if (mounted) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error:
              error instanceof Error
                ? error
                : new Error('Authentication initialization failed'),
          }));
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [initialProvider]);

  /**
   * Login action
   */
  const login = useCallback(async (options?: LoginOptions) => {
    const adapter = providerRegistry.getActive();
    if (!adapter) {
      throw new Error('No authentication provider configured');
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await adapter.login(options);

      // Refresh user state after login
      const user = await adapter.getUser();
      const roles = await adapter.getRoles();

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: true,
        user,
        roles,
        error: null,
      }));
    } catch (error) {
      console.error('[AuthProvider] Login error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Login failed'),
      }));
      throw error;
    }
  }, []);

  /**
   * Logout action
   */
  const logout = useCallback(async () => {
    const adapter = providerRegistry.getActive();
    if (!adapter) {
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await adapter.logout();

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        roles: [],
        error: null,
      }));
    } catch (error) {
      console.error('[AuthProvider] Logout error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Logout failed'),
      }));
      throw error;
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    const adapter = providerRegistry.getActive();
    if (!adapter || !adapter.isAuthenticated()) {
      return;
    }

    try {
      const user = await adapter.getUser();
      const roles = await adapter.getRoles();

      setState((prev) => ({
        ...prev,
        user,
        roles,
      }));
    } catch (error) {
      console.error('[AuthProvider] Refresh user error:', error);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Switch authentication provider
   */
  const switchProvider = useCallback((providerType: AuthProviderType) => {
    try {
      providerRegistry.setActive(providerType);
      setState((prev) => ({
        ...prev,
        providerType,
        // Reset auth state when switching providers
        isAuthenticated: false,
        user: null,
        roles: [],
      }));
    } catch (error) {
      console.error('[AuthProvider] Switch provider error:', error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error
            : new Error('Failed to switch provider'),
      }));
    }
  }, []);

  /**
   * Memoized context value
   */
  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isLoading: state.isLoading,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      roles: state.roles,
      error: state.error,
      providerType: state.providerType,
      login,
      logout,
      refreshUser,
      clearError,
      switchProvider,
    }),
    [
      state.isLoading,
      state.isAuthenticated,
      state.user,
      state.roles,
      state.error,
      state.providerType,
      login,
      logout,
      refreshUser,
      clearError,
      switchProvider,
    ]
  );

  // Show loading state while MSAL initializes
  if (state.isLoading && !state.msalInstance) {
    return (
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    );
  }

  // Wrap with MsalProvider when MSAL is available
  if (state.msalInstance) {
    return (
      <MsalProvider instance={state.msalInstance}>
        <AuthContext.Provider value={contextValue}>
          {children}
        </AuthContext.Provider>
      </MsalProvider>
    );
  }

  // Fallback without MSAL wrapper (for non-Entra providers)
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
